import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle2, ArrowUpRight, Link2, Check, Linkedin, Facebook, X, Loader2, MapPin, Briefcase } from 'lucide-react';
import useT from '../../../i18n';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSectorList, useSectorCategories } from '../../../config/sectors';

// Norske alias-slugger for kampanjelenker → kanonisk kort-id
export const SECTOR_HASH_ALIASES = {
  'sektorer-helse': 'sektorer-health',
  'sektorer-omsorg': 'sektorer-health',
  'sektorer-sesong': 'sektorer-sesong',
};

// Sektorer med egen landingsside (/sektor/<id>) + delbar OG-preview.
// Sesong har bevisst ingen egen side — kortet viser derfor ikke dele-/sideknapper.
const SECTOR_PAGE_IDS = new Set(['health', 'oppvekst']);

const STR = {
  no: {
    sub: 'Helse og omsorg er kjernen i Norvig — fra sykehjem og hjemmetjeneste til helsehus. I tillegg dekker vi oppvekst og utdanning. Direkte, uten mellomledd og uten gebyrer.',
    activeJobs: 'Aktive stillinger',
    sectorsLabel: 'Sektorer',
    municipalities: 'Kommuner dekket',
    registered: 'Registrerte fagarbeidere',
    candCount: (n) => `${n} ${n === 1 ? 'kandidat' : 'kandidater'}`,
    areas: 'områder',
    openCount: (n) => `${n} ledige`,
    soonOpen: 'Snart ledige',
    professions: 'Yrker',
    exploreSector: 'Utforsk sektoren',
    copyLink: 'Kopier delingslenke til sektoren',
    copied: 'Lenke kopiert',
    shareLinkedIn: 'Del sektoren på LinkedIn',
    shareFacebook: 'Del sektoren på Facebook',
    teaserTitle: (label) => `Kandidater — ${label}`,
    teaserSub: 'Et anonymisert utvalg av registrerte fagarbeidere i sektoren.',
    teaserLoading: 'Henter kandidater …',
    teaserEmpty: 'Ingen kandidatkort å vise ennå.',
    teaserError: 'Kunne ikke hente kandidater. Prøv igjen senere.',
    teaserYears: (n) => `${n} års erfaring`,
    teaserAvailable: 'Tilgjengelig',
    teaserCta: 'Registrer kommune for å se fulle profiler',
    teaserClose: 'Lukk',
    teaserAll: 'Alle yrker',
    teaserMore: 'Se flere',
    sectorPageLink: 'Sektorside',
  },
  en: {
    sub: 'Health and care is the core of Norvig — from nursing homes and home care to health centres. We also cover education and childcare. Direct, without middlemen or fees.',
    activeJobs: 'Active positions',
    sectorsLabel: 'Sectors',
    municipalities: 'Municipalities covered',
    registered: 'Registered professionals',
    candCount: (n) => `${n} ${n === 1 ? 'candidate' : 'candidates'}`,
    areas: 'areas',
    openCount: (n) => `${n} open`,
    soonOpen: 'Opening soon',
    professions: 'Professions',
    exploreSector: 'Explore the sector',
    copyLink: 'Copy share link to this sector',
    copied: 'Link copied',
    shareLinkedIn: 'Share this sector on LinkedIn',
    shareFacebook: 'Share this sector on Facebook',
    teaserTitle: (label) => `Candidates — ${label}`,
    teaserSub: 'An anonymised selection of registered professionals in this sector.',
    teaserLoading: 'Loading candidates …',
    teaserEmpty: 'No candidate cards to show yet.',
    teaserError: 'Could not load candidates. Please try again later.',
    teaserYears: (n) => `${n} years of experience`,
    teaserAvailable: 'Available',
    teaserCta: 'Register your company to see full profiles',
    teaserClose: 'Close',
    teaserAll: 'All professions',
    teaserMore: 'Show more',
    sectorPageLink: 'Sector page',
  },
};

/**
 * Sektorer — integrert seksjon på hovedsiden (erstatter den gamle /sektorer-siden).
 * Full sektoroversikt gruppert etter kategori, med live stillingstall,
 * yrkeschips, fordeler og delbare dybdelenker per kort (/#sektorer-<id>).
 */
export default function Sectors({ onNavigate }) {
  const t = useT();
  const s = t.landingV2.sectors;
  const { language } = useLanguage();
  const tx = STR[language === 'en' ? 'en' : 'no'];
  const sectorList = useSectorList();
  const sectorCategories = useSectorCategories();
  const [stats, setStats] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [teaserSector, setTeaserSector] = useState(null);
  const [teasers, setTeasers] = useState(null);
  const [teaserMeta, setTeaserMeta] = useState({ professions: [], hasMore: false, offset: 0 });
  const [teaserFilter, setTeaserFilter] = useState(null);
  const [teaserLoadingMore, setTeaserLoadingMore] = useState(false);
  const [teaserAppendError, setTeaserAppendError] = useState(false);
  const copyTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const fetchTeasers = (item, prof, offset, append) => {
    setTeaserAppendError(false);
    if (append) setTeaserLoadingMore(true);
    else setTeasers(null);
    const params = new URLSearchParams();
    if (prof) params.set('profession', prof);
    if (offset) params.set('offset', String(offset));
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/sectors/${item.id}/candidate-teasers?${params}`)
      .then((r) => {
        const rows = r.data?.teasers || [];
        setTeasers((prev) => (append && Array.isArray(prev) ? [...prev, ...rows] : rows));
        setTeaserMeta({
          professions: r.data?.professions || [],
          hasMore: !!r.data?.has_more,
          offset: offset + rows.length,
        });
      })
      .catch(() => {
        if (!append) setTeasers('error');
        else setTeaserAppendError(true);
      })
      .finally(() => setTeaserLoadingMore(false));
  };

  const openTeasers = (e, item) => {
    e.stopPropagation();
    setTeaserSector(item);
    setTeaserFilter(null);
    fetchTeasers(item, null, 0, false);
  };

  const applyTeaserFilter = (prof) => {
    setTeaserFilter(prof);
    fetchTeasers(teaserSector, prof, 0, false);
  };

  const professionLabel = (id) =>
    teaserSector?.professions?.find((p) => p.id === id)?.label || id;

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/sectors/stats`)
      .then((r) => setStats(r.data || {}))
      .catch(() => {});
  }, []);

  // Dybdelenke: /#sektorer-<id> markerer kortet kort etter innlasting
  useEffect(() => {
    const raw = (window.location.hash || '').slice(1);
    const target = SECTOR_HASH_ALIASES[raw] || raw;
    const m = target.match(/^sektorer-(.+)$/);
    if (!m) return;
    setHighlightedId(m[1]);
    const timer = setTimeout(() => setHighlightedId(null), 3500);
    return () => clearTimeout(timer);
  }, []);

  // Delingslenke med server-rendret OG-preview (bilde+tekst for LinkedIn/Facebook);
  // redirecter mennesker videre til /#sektorer-<id>
  const shareUrlFor = (id) =>
    `${window.location.origin}/api/share/sektor/${id}${language === 'en' ? '?lang=en' : ''}`;

  const openShare = (e, url) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyCardLink = async (e, id) => {
    e.stopPropagation();
    const url = shareUrlFor(id);
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) {
      setCopiedId(id);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedId((v) => (v === id ? null : v)), 2000);
    }
  };

  const totalJobs = typeof stats.total === 'number'
    ? stats.total
    : Object.entries(stats)
        .filter(([k, v]) => k !== 'candidates' && typeof v === 'number')
        .reduce((sum, [, n]) => sum + n, 0);
  const candidateStats = stats.candidates || {};
  const totalCandidates = Object.values(candidateStats).reduce((sum, n) => sum + (n || 0), 0);

  return (
    <section
      id="sektorer"
      className="bg-paper py-section border-t border-ink/[0.06] scroll-mt-20"
      data-testid="landing-sectors"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="font-sans text-xs uppercase tracking-widest text-ink/50">
            {s.eyebrow}
          </p>
          <h2 className="mt-6 font-display font-light text-display-md text-ink">
            {s.title}
          </h2>
          <p className="mt-6 font-sans text-base text-ink/60 leading-relaxed">
            {tx.sub}
          </p>
        </div>

        {/* Live counter strip */}
        <div
          className="mt-12 flex flex-wrap items-end gap-x-14 gap-y-6"
          data-testid="landing-sectors-stats"
        >
          <div>
            <div className="font-display font-light text-5xl text-ink" data-testid="landing-sectors-total-jobs">
              {totalJobs > 0 ? totalJobs : '—'}
            </div>
            <div className="mt-1 font-sans text-xs uppercase tracking-widest text-ink/50">
              {tx.activeJobs}
            </div>
          </div>
          <div>
            <div className="font-display font-light text-5xl text-ink">
              {sectorList.length}
            </div>
            <div className="mt-1 font-sans text-xs uppercase tracking-widest text-ink/50">
              {tx.sectorsLabel}
            </div>
          </div>
          <div>
            <div className="font-display font-light text-5xl text-ink" data-testid="landing-sectors-total-candidates">
              {totalCandidates > 0 ? totalCandidates : '—'}
            </div>
            <div className="mt-1 font-sans text-xs uppercase tracking-widest text-ink/50">
              {tx.registered}
            </div>
          </div>
          <div>
            <div className="font-display font-light text-5xl text-fjord">356</div>
            <div className="mt-1 font-sans text-xs uppercase tracking-widest text-ink/50">
              {tx.municipalities}
            </div>
          </div>
        </div>

        {/* Categories with full sector cards */}
        <div className="mt-16 space-y-20">
          {Object.values(sectorCategories).map((cat, catIdx) => {
            const sectorsInCat = sectorList.filter((x) => x.category === cat.id);
            if (!sectorsInCat.length) return null;
            return (
              <div key={cat.id} data-testid={`landing-sektor-cat-${cat.id}`}>
                <div className="flex items-baseline gap-4 mb-8">
                  <span className="font-sans text-xs uppercase tracking-widest text-fjord">
                    {String(catIdx + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-display text-2xl text-ink">{cat.label}</h3>
                  <div className="h-px flex-1 bg-ink/10" />
                  <span className="hidden md:inline font-sans text-xs uppercase tracking-widest text-ink/40">
                    {sectorsInCat.length} {tx.areas}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {sectorsInCat.map((item, i) => {
                    const Icon = item.icon;
                    const count = stats[item.id] || 0;
                    const candCount = candidateStats[item.id] || 0;
                    const sectorNum = String(
                      sectorList.findIndex((x) => x.id === item.id) + 1
                    ).padStart(2, '0');
                    const isHighlighted = highlightedId === item.id;
                    return (
                      <motion.div
                        key={item.id}
                        id={`sektorer-${item.id}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onNavigate?.(`/jobs?sector=${item.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onNavigate?.(`/jobs?sector=${item.id}`);
                          }
                        }}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
                        className={`group relative text-left cursor-pointer scroll-mt-24 bg-paper-warm border transition-colors p-8 md:p-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ${
                          isHighlighted
                            ? 'border-fjord ring-2 ring-fjord/40'
                            : 'border-ink/[0.08] hover:border-ink/20'
                        }`}
                        data-testid={`sector-card-${item.id}`}
                      >
                        {item.image && (
                          <div className="-mx-8 -mt-8 md:-mx-10 md:-mt-10 mb-7 overflow-hidden">
                            <img
                              src={item.image}
                              alt={item.label}
                              loading="lazy"
                              className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                              data-testid={`sector-card-image-${item.id}`}
                            />
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <span className="font-sans text-xs tracking-widest text-ink/40">
                              {sectorNum}
                            </span>
                            {Icon && <Icon size={18} style={{ color: item.accent }} />}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-sans text-xs uppercase tracking-widest text-fjord">
                              {count > 0 ? tx.openCount(count) : tx.soonOpen}
                              {candCount > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => openTeasers(e, item)}
                                  className="text-ink/50 hover:text-fjord underline decoration-dotted underline-offset-2 transition-colors"
                                  aria-label={tx.teaserTitle(item.label)}
                                  data-testid={`sector-card-candidates-${item.id}`}
                                >
                                  {' · '}{tx.candCount(candCount)}
                                </button>
                              )}
                            </span>
                            {SECTOR_PAGE_IDS.has(item.id) && (
                              <>
                            <button
                              type="button"
                              onClick={(e) => openShare(e, `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrlFor(item.id))}`)}
                              aria-label={tx.shareLinkedIn}
                              title={tx.shareLinkedIn}
                              className="p-1.5 -m-1.5 rounded-md text-ink/40 hover:text-fjord transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40"
                              data-testid={`sector-card-share-linkedin-${item.id}`}
                            >
                              <Linkedin size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => openShare(e, `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrlFor(item.id))}`)}
                              aria-label={tx.shareFacebook}
                              title={tx.shareFacebook}
                              className="p-1.5 -m-1.5 rounded-md text-ink/40 hover:text-fjord transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40"
                              data-testid={`sector-card-share-facebook-${item.id}`}
                            >
                              <Facebook size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => copyCardLink(e, item.id)}
                              aria-label={copiedId === item.id ? tx.copied : tx.copyLink}
                              title={copiedId === item.id ? tx.copied : tx.copyLink}
                              className="inline-flex items-center gap-1.5 p-1.5 -m-1.5 rounded-md text-ink/40 hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40"
                              data-testid={`sector-card-copylink-${item.id}`}
                            >
                              {copiedId === item.id ? (
                                <>
                                  <Check size={14} className="text-fjord" />
                                  <span className="font-sans text-[10px] uppercase tracking-widest text-fjord">
                                    {tx.copied}
                                  </span>
                                </>
                              ) : (
                                <Link2 size={14} />
                              )}
                            </button>
                            <a
                              href={`/sektor/${item.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 font-sans text-[10px] uppercase tracking-widest text-ink/50 hover:text-fjord transition-colors"
                              data-testid={`sector-card-page-link-${item.id}`}
                            >
                              {tx.sectorPageLink}
                              <ArrowUpRight size={12} />
                            </a>
                              </>
                            )}
                          </div>
                        </div>

                        <p
                          className="font-display italic font-light text-lg leading-tight mb-2"
                          style={{ color: item.accent }}
                        >
                          {item.tagline}
                        </p>

                        <h4 className="font-display text-2xl md:text-3xl text-ink">
                          {item.label}
                        </h4>

                        <p className="mt-4 font-sans text-sm text-ink/60 leading-relaxed">
                          {item.longDescription}
                        </p>

                        <div className="mt-6">
                          <div className="font-sans text-[10px] uppercase tracking-widest text-ink/40 mb-2.5">
                            {tx.professions}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {item.professions.slice(0, 5).map((p) => (
                              <span
                                key={p.id}
                                className="inline-flex px-3 py-1 rounded-full text-xs font-sans text-ink/70 border border-ink/10 bg-paper"
                              >
                                {p.label}
                              </span>
                            ))}
                            {item.professions.length > 5 && (
                              <span className="inline-flex px-3 py-1 rounded-full text-xs font-sans text-ink/40">
                                +{item.professions.length - 5}
                              </span>
                            )}
                          </div>
                        </div>

                        <ul className="mt-6 space-y-2">
                          {item.benefits.slice(0, 3).map((b, bi) => (
                            <li
                              key={bi}
                              className="flex items-start gap-2 font-sans text-sm text-ink/70"
                            >
                              <CheckCircle2
                                size={14}
                                className="flex-shrink-0 mt-0.5"
                                style={{ color: item.accent }}
                              />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="mt-8 inline-flex items-center gap-1.5 font-sans text-xs uppercase tracking-widest font-medium text-ink">
                          <span>{tx.exploreSector}</span>
                          <ArrowUpRight
                            size={14}
                            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </div>

                        <div
                          className="absolute bottom-0 left-0 right-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                          style={{ background: item.accent }}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Anonymisert kandidat-teaser modal */}
      {teaserSector && (
        <div
          className="fixed inset-0 z-50 grid place-items-center px-4 py-8 overflow-y-auto"
          style={{ background: 'rgba(10,14,27,0.55)' }}
          onClick={() => setTeaserSector(null)}
          data-testid="sector-teaser-modal"
        >
          <div
            className="max-w-2xl w-full bg-paper border border-ink/10 p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={tx.teaserTitle(teaserSector.label)}
          >
            <div className="flex items-start justify-between gap-4 mb-2">
              <h3 className="font-display text-2xl text-ink">{tx.teaserTitle(teaserSector.label)}</h3>
              <button
                onClick={() => setTeaserSector(null)}
                aria-label={tx.teaserClose}
                className="p-1.5 text-ink/50 hover:text-ink transition-colors"
                data-testid="sector-teaser-close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="font-sans text-sm text-ink/60 mb-6">{tx.teaserSub}</p>

            {teaserMeta.professions.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="sector-teaser-filters">
                <button
                  onClick={() => applyTeaserFilter(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-sans font-medium border transition-colors ${
                    teaserFilter === null ? 'bg-ink text-paper border-ink' : 'text-ink/60 border-ink/15 hover:border-ink/40'
                  }`}
                  data-testid="sector-teaser-filter-all"
                >
                  {tx.teaserAll}
                </button>
                {teaserMeta.professions.map((p) => (
                  <button
                    key={p.profession}
                    onClick={() => applyTeaserFilter(p.profession)}
                    className={`px-3 py-1.5 rounded-full text-xs font-sans font-medium border transition-colors ${
                      teaserFilter === p.profession ? 'bg-ink text-paper border-ink' : 'text-ink/60 border-ink/15 hover:border-ink/40'
                    }`}
                    data-testid={`sector-teaser-filter-${p.profession}`}
                  >
                    <span className="capitalize">{professionLabel(p.profession)}</span> · {p.count}
                  </button>
                ))}
              </div>
            )}

            {teasers === null && (
              <div className="flex items-center gap-2 font-sans text-sm text-ink/50 py-8" data-testid="sector-teaser-loading">
                <Loader2 size={15} className="animate-spin" /> {tx.teaserLoading}
              </div>
            )}
            {teasers === 'error' && (
              <p className="font-sans text-sm py-8" style={{ color: '#B54B3A' }} data-testid="sector-teaser-error">{tx.teaserError}</p>
            )}
            {Array.isArray(teasers) && teasers.length === 0 && (
              <p className="font-sans text-sm text-ink/50 py-8" data-testid="sector-teaser-empty">{tx.teaserEmpty}</p>
            )}
            {Array.isArray(teasers) && teasers.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sector-teaser-grid">
                {teasers.map((c, i) => (
                  <div key={i} className="bg-paper-warm border border-ink/[0.08] p-5" data-testid={`sector-teaser-card-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="inline-flex items-center gap-1.5 font-sans text-sm font-medium text-ink capitalize">
                        <Briefcase size={13} style={{ color: teaserSector.accent }} />
                        {professionLabel(c.profession)}
                      </span>
                      {c.availability && (
                        <span className="inline-flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-widest text-fjord">
                          <span className="w-1.5 h-1.5 rounded-full bg-fjord" aria-hidden />
                          {tx.teaserAvailable}
                        </span>
                      )}
                    </div>
                    <div className="font-sans text-xs text-ink/60 space-y-1">
                      {typeof c.experience_years === 'number' && <div>{tx.teaserYears(c.experience_years)}</div>}
                      {c.location && (
                        <div className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {c.location}
                        </div>
                      )}
                    </div>
                    {(c.languages?.length > 0 || c.skills?.length > 0) && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(c.languages || []).map((l) => (
                          <span key={l} className="px-2 py-0.5 rounded-full text-[11px] font-sans text-ink/60 border border-ink/10 bg-paper">{l}</span>
                        ))}
                        {(c.skills || []).map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded-full text-[11px] font-sans border bg-paper" style={{ color: teaserSector.accent, borderColor: `${teaserSector.accent}44` }}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(teasers) && teaserMeta.hasMore && (
              <button
                onClick={() => fetchTeasers(teaserSector, teaserFilter, teaserMeta.offset, true)}
                disabled={teaserLoadingMore}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 border border-ink/15 font-sans text-sm font-medium text-ink hover:border-ink/40 transition-colors disabled:opacity-50"
                data-testid="sector-teaser-more-btn"
              >
                {teaserLoadingMore && <Loader2 size={13} className="animate-spin" />}
                {tx.teaserMore}
              </button>
            )}
            {teaserAppendError && (
              <p className="mt-3 font-sans text-xs" style={{ color: '#B54B3A' }} data-testid="sector-teaser-more-error">
                {tx.teaserError}
              </p>
            )}

            <button
              onClick={() => onNavigate?.('/login?next=%2Fprofiles')}
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 bg-ink text-paper font-sans text-sm font-medium hover:bg-ink/90 transition-colors"
              data-testid="sector-teaser-cta"
            >
              {tx.teaserCta}
              <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
